/**
 * Post-login redirects:
 * - localStorage `redirectAfterLogin` (recruiter email → /login?redirect=… or /signin?redirect=…)
 * - sessionStorage `postLoginRedirect` (interview deep links — tab-scoped)
 */

import { decodeToken, getAuthToken } from '@/lib/auth'
import {
  isEnterpriseLoginEnabled,
  redirectPathTargetsEnterprise,
} from '@/lib/enterpriseLoginFeature'

export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

/** localStorage key — cleared after successful read (one-time use). */
export const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin'

export function setPostLoginRedirect(path: string): void {
  if (typeof window === 'undefined') return
  if (typeof path === 'string' && path.trim() !== '') {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path)
  }
}

export function isSafeInternalPath(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (trimmed === '') return false
  if (!trimmed.startsWith('/')) return false
  if (trimmed.startsWith('//')) return false
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return false
  }
  return true
}

export function getRedirectPathname(path: string): string {
  const noHash = path.split('#')[0] ?? ''
  const noQuery = noHash.split('?')[0] ?? ''
  return noQuery.trim() || '/'
}

export function pathRequiresRecruiterAccess(path: string): boolean {
  if (!isEnterpriseLoginEnabled()) return false
  const p = getRedirectPathname(path)
  return (
    p === '/enterprise/dashboard' ||
    p.startsWith('/enterprise/dashboard/') ||
    p === '/enterprise/recruiters' ||
    p.startsWith('/enterprise/recruiters/')
  )
}

/** Map legacy invite/bookmark paths (removed route) to the enterprise recruiter UI. */
export function mapLegacyRecruiterDashboardPath(path: string): string {
  const trimmed = path.trim()
  const pathname = getRedirectPathname(trimmed)
  if (pathname === '/recruiter/dashboard' || pathname.startsWith('/recruiter/dashboard/')) {
    if (!isEnterpriseLoginEnabled()) {
      return DEFAULT_DASHBOARD_PATH
    }
    if (pathname === '/recruiter/dashboard') return '/enterprise/dashboard'
    return `/enterprise/dashboard${pathname.slice('/recruiter/dashboard'.length)}`
  }
  return trimmed
}

/** Map legacy /jd-interview bookmarks to unified /interview. */
export function mapLegacyJdInterviewPath(path: string): string {
  const trimmed = path.trim()
  const pathname = getRedirectPathname(trimmed)
  if (pathname === '/jd-interview') {
    return '/interview'
  }
  return trimmed
}

/** Roles that may access /enterprise/* without calling recruiter list APIs. */
function jwtRoleHasEnterpriseDashboardAccess(role: string | undefined): boolean {
  return role === 'recruiter' || role === 'enterprise'
}

export function getAndClearPostLoginRedirect(): string | null {
  if (typeof window === 'undefined') return null
  const value = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  return value
}

/** Remove session post-login redirect without reading (e.g. user closed login modal). */
export function clearPostLoginRedirect(): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
}

export function captureRedirectQueryParam(redirect: string | null | undefined): void {
  if (typeof window === 'undefined') return
  if (!redirect || !isSafeInternalPath(redirect)) return
  if (!isEnterpriseLoginEnabled() && redirectPathTargetsEnterprise(redirect)) return
  localStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, redirect.trim())
}

const DEFAULT_DASHBOARD_PATH = '/dashboard'
const RECRUITER_DASHBOARD_PATH = '/enterprise/dashboard'
const SKILL_ASSESSMENT_PATH = '/skill-assessment'

export function getAndClearRedirectAfterLogin(): string | null {
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem(REDIRECT_AFTER_LOGIN_KEY)
  if (raw == null) return null
  localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY)
  return isSafeInternalPath(raw) ? raw.trim() : null
}

function takePostLoginRedirectIfValid(): string | null {
  if (typeof window === 'undefined') return null
  const raw = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
  if (raw == null) return null
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  return isSafeInternalPath(raw) ? raw.trim() : null
}

export async function resolvePostLoginRedirectOrDashboardAsync(options?: {
  skillAssessmentRedirect?: boolean
}): Promise<string> {
  const token = getAuthToken()
  if (token && isEnterpriseLoginEnabled()) {
    const payload = decodeToken(token) as { role?: string } | null
    if (jwtRoleHasEnterpriseDashboardAccess(payload?.role)) {
      return RECRUITER_DASHBOARD_PATH
    }
  }

  let candidate = getAndClearRedirectAfterLogin()
  if (candidate == null) {
    candidate = takePostLoginRedirectIfValid()
  }
  if (candidate != null) {
    candidate = mapLegacyJdInterviewPath(mapLegacyRecruiterDashboardPath(candidate))
    if (!isEnterpriseLoginEnabled() && redirectPathTargetsEnterprise(candidate)) {
      candidate = DEFAULT_DASHBOARD_PATH
    }
  }

  if (candidate != null) {
    if (pathRequiresRecruiterAccess(candidate)) {
      const payload = token ? (decodeToken(token) as { role?: string } | null) : null
      if (!jwtRoleHasEnterpriseDashboardAccess(payload?.role)) {
        return DEFAULT_DASHBOARD_PATH
      }
    }
    return candidate
  }

  if (options?.skillAssessmentRedirect) {
    return SKILL_ASSESSMENT_PATH
  }

  // Candidate sessions use role "user" (or legacy tokens without role). Avoid GET /recruiter/jds
  // on every login; enterprise users must have recruiter|enterprise in the JWT.
  return DEFAULT_DASHBOARD_PATH
}

/** Session-only fallback (e.g. legacy); prefer resolvePostLoginRedirectOrDashboardAsync after login. */
export function getPostLoginRedirectOrDashboard(): string {
  const raw = getAndClearPostLoginRedirect()
  if (!isSafeInternalPath(raw)) return DEFAULT_DASHBOARD_PATH
  let path = mapLegacyJdInterviewPath(mapLegacyRecruiterDashboardPath(raw))
  if (!isEnterpriseLoginEnabled() && redirectPathTargetsEnterprise(path)) {
    return DEFAULT_DASHBOARD_PATH
  }
  return path
}
